import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Box } from '@mui/material';
import { useDispatch } from 'react-redux';
import { useEffect, useMemo, useContext } from 'react';

import {
  resetNavigationAction,
  setNavigationAction,
} from 'app/shared-components/store/headerSlice';
import { Tabs } from 'app/shared-components/structure';
import useHeaderTitle from 'app/hooks/useHeaderTitle';

import { GroupServiceContext } from '../context/GroupServiceContext';

const tabs = [
  {
    label: 'Settings',
    value: 'settings',
    path: '../settings',
  },
  {
    label: 'Content',
    value: 'content',
    path: '../content',
  },
  {
    label: 'Payment',
    value: 'payments',
    path: '../payments',
    badge: 'New',
  },
  {
    label: 'Pharmacies',
    value: 'pharmacies',
    path: '../pharmacies',
  },
];

const CONSULTATION_EXCLUDED_TABS = ['content', 'payments'];

export default function GroupServiceDetailsLayout({ children }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { organisationId } = useParams();

  const { serviceTemplate = {} } = useContext(GroupServiceContext);
  const isConsultation = serviceTemplate?.type === 'Consultation';

  const processedTabs = useMemo(() => {
    if (!isConsultation) return tabs;

    return tabs.filter((tab) => !CONSULTATION_EXCLUDED_TABS.includes(tab.value));
  }, [isConsultation]);

  const selectedTab = useMemo(
    () =>
      processedTabs.find((tab) => location.pathname.endsWith(tab.value))?.value ?? tabs[0].value,
    [location.pathname, processedTabs]
  );

  useEffect(() => {
    // If current URL points to a tab that is not available for Consultations, normalise the URL.
    const urlMatchesTab = processedTabs.some((tab) => location.pathname.endsWith(tab.value));
    if (!urlMatchesTab) navigate(processedTabs[0].path, { replace: true, relative: 'path' });
  }, [location.pathname, processedTabs, navigate]);

  useHeaderTitle(serviceTemplate?.customName || serviceTemplate?.name);

  useEffect(() => {
    const backUrl = organisationId
      ? `/services/service-templates?organisationId=${organisationId}`
      : '/services/service-templates';

    dispatch(
      setNavigationAction({
        title: 'Group Services',
        type: 'button',
        callback: () => navigate(backUrl),
      })
    );

    return () => dispatch(resetNavigationAction());
  }, [dispatch, navigate, organisationId]);

  function handleTabChange(event, newValue) {
    const newPath = processedTabs.find((tab) => tab.value === newValue)?.path;
    if (newPath) navigate(newPath, { replace: true, relative: 'path' });
  }

  return (
    <Box className="grow flex flex-col px-20 py-16 gap-16 bg-white overflow-hidden">
      <Tabs tabs={processedTabs} selectedTab={selectedTab} handleChange={handleTabChange} />
      {children}
    </Box>
  );
}
